import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Exige sesión para entrar a cualquier ruta protegida, renovando el access token si caducó.
 *
 * Sin refresh token o con el refresh caducado cierra sesión y manda al login. Si el backend está
 * caído deja pasar sin validar, para que el usuario siga viendo la app en modo sin conexión en lugar
 * de ser expulsado por una caída transitoria; por la misma razón un error de red al renovar también deja pasar.
 * @param _route - Ruta destino; no se usa.
 * @param _state - Estado del router; no se usa.
 * @returns `true`/`false` directo, o un Observable que emite `true` si el refresh tuvo éxito (o falló
 *   por red) y `false` tras cerrar sesión si el refresh fue rechazado.
 */
export const authGuard: CanActivateFn = (_route, _state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (authService.isLoggedIn() && !authService.isTokenExpired()) {
        return true;
    }

    if (!authService.getRefreshToken()) {
        authService.logout();
        router.navigate(['/login']);
        return false;
    }

    if (authService.backendOffline()) {
        return true;
    }

    if (authService.isRefreshTokenExpired()) {
        authService.logout();
        router.navigate(['/login']);
        return false;
    }

    return authService.refreshToken().pipe(
        map(() => true),
        catchError((err) => {
            if (authService.isNetworkError(err)) {
                return of(true);
            }
            authService.logout();
            router.navigate(['/login']);
            return of(false);
        })
    );
};
